import { Component, Input, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { marketingBlogEntity, marketingSocialPostEntity, marketingContentStatusEntity, marketingEmployeeSocialProfileEntity } from 'mj_generatedentities';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { RunView } from '@memberjunction/core';

interface EmployeeSocialProfileRow {
    ID: string;
    EmployeeID: string;
    DisplayName: string;
    Status: string;
    SocialPlatformID: string;
    ToneNotes?: string | null;
}

type RowState = 'idle' | 'busy' | 'error';

@Component({
    standalone: false,
    selector: 'mkt-social-queue-section',
    template: `
    <div class="sq">
        <h2 class="sq__title"><i class="fa-brands fa-linkedin"></i> Social Queue</h2>
        <p class="sq__subtitle">Generate LinkedIn variations from a published blog, then review and approve them as Buffer drafts.</p>

        <!-- ── Generate bar ─────────────────────────────── -->
        <div class="sq__gen">
            <div class="sq__gen-row">
                <label class="sq__label">Posting as</label>
                <select class="sq__select" [(ngModel)]="selectedProfileId" (ngModelChange)="onProfileChange()">
                    <option value="">Select a profile...</option>
                    <option *ngFor="let p of employeeSocialProfiles" [value]="p.ID">{{ p.DisplayName }}</option>
                </select>

                <label class="sq__label sq__label--tight">Variations</label>
                <input class="sq__count" type="number" min="1" max="20" [(ngModel)]="variationCount">

                <button class="sq__btn sq__btn--primary sq__gen-btn"
                        [disabled]="!canGenerate || generating"
                        (click)="generate()">
                    <i *ngIf="!generating" class="fa-solid fa-wand-magic-sparkles"></i>
                    <i *ngIf="generating" class="fa-solid fa-spinner fa-spin"></i>
                    <ng-container *ngIf="!generating">
                        <span *ngIf="selectedBlogIds.length === 0">Generate & queue</span>
                        <span *ngIf="selectedBlogIds.length > 0">Generate & queue {{ selectedBlogIds.length * variationCount }} variation{{ selectedBlogIds.length * variationCount !== 1 ? 's' : '' }}</span>
                    </ng-container>
                    <span *ngIf="generating">Generating...</span>
                </button>
            </div>

            <!-- No profiles empty state -->
            <div class="sq__profile-empty" *ngIf="employeeSocialProfiles.length === 0">
                <i class="fa-solid fa-circle-info"></i>
                No active employee profiles for this company. Switch companies via the top-right selector, or seed an EmployeeSocialProfile row.
            </div>

            <!-- Tone editor (inline, below profile) -->
            <div class="sq__tone-bar" *ngIf="selectedProfileId && !toneEditorOpen">
                <span class="sq__tone-preview" *ngIf="selectedProfileTone">
                    <span class="sq__tone-label">Voice guidance:</span>
                    {{ selectedProfileTone.length > 120 ? (selectedProfileTone.substring(0, 120) + '…') : selectedProfileTone }}
                </span>
                <span class="sq__tone-preview sq__tone-preview--empty" *ngIf="!selectedProfileTone">
                    <span class="sq__tone-label">Voice guidance:</span> <em>(none set — LLM will use generic professional voice)</em>
                </span>
                <button class="sq__btn sq__btn--small sq__tone-edit-btn" (click)="openToneEditor()">
                    <i class="fa-solid fa-pen"></i> Edit
                </button>
            </div>

            <div class="sq__tone-edit" *ngIf="toneEditorOpen">
                <div class="sq__tone-edit-head">
                    <span class="sq__label">Voice guidance for {{ selectedProfileName }}</span>
                    <span class="sq__tone-hint">Voice rules, do's & don'ts, and sample posts. The LLM reads this verbatim before writing.</span>
                </div>
                <textarea class="sq__tone-textarea" [(ngModel)]="toneEditBody" rows="12"
                          placeholder="## Voice rules&#10;- First person, direct, no corporate distance&#10;- ...&#10;&#10;## Sample posts (match this voice)&#10;---&#10;[paste post 1]&#10;---&#10;[paste post 2]"></textarea>
                <div class="sq__tone-edit-actions">
                    <button class="sq__btn sq__btn--primary sq__btn--small" [disabled]="toneSaving" (click)="saveTone()">
                        <i *ngIf="!toneSaving" class="fa-solid fa-floppy-disk"></i>
                        <i *ngIf="toneSaving" class="fa-solid fa-spinner fa-spin"></i>
                        Save tone
                    </button>
                    <button class="sq__btn sq__btn--small" [disabled]="toneSaving" (click)="cancelToneEdit()">Cancel</button>
                    <span *ngIf="toneError" class="sq__tone-error">{{ toneError }}</span>
                </div>
            </div>

            <div class="sq__gen-row sq__gen-row--blog">
                <div class="sq__blog-picker">
                    <div class="sq__blog-chips" *ngIf="selectedBlogIds.length > 0">
                        <span class="sq__blog-chips-label">Selected ({{ selectedBlogIds.length }}):</span>
                        <span *ngFor="let blog of selectedBlogEntities" class="sq__blog-chip">
                            <span class="sq__blog-chip-title">{{ blog.Title }}</span>
                            <button class="sq__blog-chip-x" (click)="toggleBlog(blog.ID)" title="Remove">
                                <i class="fa-solid fa-xmark"></i>
                            </button>
                        </span>
                        <button class="sq__btn sq__btn--small sq__blog-chips-clear" (click)="clearAllBlogs()">
                            Clear all
                        </button>
                    </div>

                    <input class="sq__search" type="text" placeholder="Filter recent blogs by title or summary..."
                           [(ngModel)]="blogSearch">
                    <div class="sq__blog-list">
                        <div *ngFor="let b of filteredBlogs"
                             class="sq__blog-item"
                             [class.sq__blog-item--selected]="isBlogSelected(b.ID)"
                             (click)="toggleBlog(b.ID)">
                            <i class="sq__blog-item-check fa-solid"
                               [ngClass]="isBlogSelected(b.ID) ? 'fa-square-check' : 'fa-square'"></i>
                            <div class="sq__blog-item-main">
                                <div class="sq__blog-item-title">
                                    {{ b.Title }}
                                    <span class="sq__blog-status" *ngIf="b.Get('Status')">{{ b.Get('Status') }}</span>
                                </div>
                                <div class="sq__blog-item-meta">
                                    <span *ngIf="b.Get('PublishDate')">{{ b.Get('PublishDate') | date:'mediumDate' }} · </span>
                                    {{ (b.Get('Summary') || '').substring(0, 140) }}{{ (b.Get('Summary')?.length || 0) > 140 ? '…' : '' }}
                                </div>
                            </div>
                        </div>
                        <div *ngIf="filteredBlogs.length === 0" class="sq__empty-small">
                            No blogs found{{ blogSearch ? ' matching "' + blogSearch + '"' : '' }}.
                        </div>
                    </div>
                </div>
            </div>

            <div class="sq__gen-progress" *ngIf="generating && generationProgress">
                <i class="fa-solid fa-spinner fa-spin"></i> {{ generationProgress }}
            </div>

            <div class="sq__gen-error" *ngIf="generateError">
                <i class="fa-solid fa-exclamation-triangle"></i>
                <span class="sq__gen-error-text">{{ generateError }}</span>
                <button class="sq__btn sq__btn--small" (click)="generateError = ''">Dismiss</button>
            </div>
        </div>

        <!-- ── Queue list ───────────────────────────────── -->
        <div class="sq__queue">
            <div class="sq__queue-head">
                <h3 class="sq__queue-title">Drafts awaiting review</h3>
                <span class="sq__queue-count">{{ draftPosts.length }} draft{{ draftPosts.length !== 1 ? 's' : '' }}</span>
            </div>

            <div *ngIf="draftPosts.length === 0" class="sq__empty">
                <i class="fa-solid fa-inbox"></i>
                <p>No employee drafts yet. Generate variations above to queue some.</p>
            </div>

            <div *ngFor="let post of draftPosts" class="sq__row" [class.sq__row--editing]="editingPostId === post.ID">
                <div class="sq__row-head">
                    <span class="sq__row-employee"><i class="fa-solid fa-user"></i> {{ employeeNameFor(post.Get('SocialProfileID')) }}</span>
                    <span class="sq__row-sep">·</span>
                    <span class="sq__row-blog">{{ blogTitleFor(post.Get('BlogID')) }}</span>
                    <span class="sq__row-sep">·</span>
                    <span class="sq__row-platform">{{ post.Get('SocialPlatform') || 'LinkedIn' }}</span>
                </div>

                <!-- Read mode -->
                <div *ngIf="editingPostId !== post.ID" class="sq__row-body">{{ post.Get('Body') }}</div>
                <img *ngIf="editingPostId !== post.ID && post.Get('FeaturedImageURL')"
                     class="sq__row-image"
                     [src]="post.Get('FeaturedImageURL')"
                     alt="Generated social post image"
                     loading="lazy" />

                <!-- Edit mode -->
                <div *ngIf="editingPostId === post.ID" class="sq__row-edit">
                    <textarea class="sq__row-textarea" [(ngModel)]="editingBody" rows="8"></textarea>
                    <div class="sq__row-edit-meta">{{ editingBody.length }} / 3000 chars</div>
                </div>

                <div class="sq__row-actions">
                    <ng-container *ngIf="editingPostId !== post.ID">
                        <button class="sq__btn sq__btn--primary sq__btn--small"
                                [disabled]="rowState[post.ID] === 'busy'"
                                (click)="approve(post)">
                            <i *ngIf="rowState[post.ID] !== 'busy'" class="fa-solid fa-check"></i>
                            <i *ngIf="rowState[post.ID] === 'busy' && rowAction[post.ID] === 'approve'" class="fa-solid fa-spinner fa-spin"></i>
                            Approve
                        </button>
                        <button class="sq__btn sq__btn--small"
                                [disabled]="rowState[post.ID] === 'busy'"
                                (click)="reject(post)">
                            <i *ngIf="rowState[post.ID] !== 'busy' || rowAction[post.ID] !== 'reject'" class="fa-solid fa-xmark"></i>
                            <i *ngIf="rowState[post.ID] === 'busy' && rowAction[post.ID] === 'reject'" class="fa-solid fa-spinner fa-spin"></i>
                            Reject
                        </button>
                        <button class="sq__btn sq__btn--small"
                                [disabled]="rowState[post.ID] === 'busy'"
                                (click)="startEdit(post)">
                            <i class="fa-solid fa-pen"></i> Edit
                        </button>
                    </ng-container>

                    <ng-container *ngIf="editingPostId === post.ID">
                        <button class="sq__btn sq__btn--primary sq__btn--small"
                                [disabled]="rowState[post.ID] === 'busy'"
                                (click)="saveEdit(post)">
                            <i *ngIf="rowState[post.ID] !== 'busy'" class="fa-solid fa-floppy-disk"></i>
                            <i *ngIf="rowState[post.ID] === 'busy'" class="fa-solid fa-spinner fa-spin"></i>
                            Save
                        </button>
                        <button class="sq__btn sq__btn--small" (click)="cancelEdit()">
                            <i class="fa-solid fa-xmark"></i> Cancel
                        </button>
                    </ng-container>

                    <span *ngIf="rowError[post.ID]" class="sq__row-error">{{ rowError[post.ID] }}</span>
                </div>
            </div>
        </div>
    </div>
    `,
    styles: [`
    .sq { padding: 0; overflow-y: auto; max-height: calc(100vh - 120px); }
    .sq__title { font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 8px; margin: 0 0 4px; color: var(--mj-text-primary); }
    .sq__subtitle { font-size: 14px; color: var(--mj-text-secondary); margin: 0 0 20px; }

    /* Generate bar */
    .sq__gen { background: var(--mj-bg-page); border: 1px solid var(--mj-border-default); border-radius: 8px; padding: 14px; margin-bottom: 24px; }
    .sq__gen-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .sq__gen-row + .sq__gen-row { margin-top: 12px; }
    .sq__gen-row--blog { position: relative; }
    .sq__label { font-size: 13px; font-weight: 600; color: var(--mj-text-secondary); white-space: nowrap; }
    .sq__label--tight { margin-left: 8px; }
    .sq__select { flex: 1; min-width: 200px; max-width: 280px; padding: 6px 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 14px; font-family: inherit; background: var(--mj-bg-surface); color: var(--mj-text-primary); }
    .sq__count { width: 64px; padding: 6px 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 14px; font-family: inherit; background: var(--mj-bg-surface); color: var(--mj-text-primary); }
    .sq__gen-btn { margin-left: auto; }

    /* Profile empty state */
    .sq__profile-empty { margin-top: 10px; padding: 10px 14px; background: var(--mj-status-warning-bg, #fff3cd); color: var(--mj-status-warning, #856404); border: 1px solid var(--mj-status-warning-border, #ffeaa7); border-radius: 6px; font-size: 13px; display: flex; align-items: center; gap: 8px; }

    /* Tone editor */
    .sq__tone-bar { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 12.5px; }
    .sq__tone-label { font-weight: 600; color: var(--mj-text-secondary); margin-right: 4px; }
    .sq__tone-preview { flex: 1; color: var(--mj-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .sq__tone-preview--empty { color: var(--mj-text-disabled); }
    .sq__tone-preview--empty em { color: var(--mj-text-disabled); font-style: italic; }
    .sq__tone-edit-btn { white-space: nowrap; }
    .sq__tone-edit { padding: 12px; background: var(--mj-bg-surface); border: 1px solid var(--mj-brand-primary); border-radius: 6px; }
    .sq__tone-edit-head { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
    .sq__tone-hint { font-size: 12px; color: var(--mj-text-secondary); }
    .sq__tone-textarea { width: 100%; padding: 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 13px; font-family: "SF Mono", Menlo, Consolas, monospace; resize: vertical; box-sizing: border-box; background: var(--mj-bg-page); color: var(--mj-text-primary); line-height: 1.5; }
    .sq__tone-textarea:focus { border-color: var(--mj-brand-primary); outline: none; }
    .sq__tone-edit-actions { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
    .sq__tone-error { font-size: 12px; color: var(--mj-status-error); }

    /* Blog picker */
    .sq__blog-picker { flex: 1; }
    .sq__search { width: 100%; padding: 8px 12px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 14px; font-family: inherit; background: var(--mj-bg-surface); color: var(--mj-text-primary); box-sizing: border-box; margin-bottom: 6px; }
    .sq__search:focus { border-color: var(--mj-brand-primary); outline: none; }
    .sq__blog-chips { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; padding: 8px 10px; margin-bottom: 8px; background: color-mix(in srgb, var(--mj-brand-primary) 6%, transparent); border: 1px solid var(--mj-brand-primary); border-radius: 6px; font-size: 12.5px; }
    .sq__blog-chips-label { font-weight: 600; color: var(--mj-text-secondary); margin-right: 4px; }
    .sq__blog-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 4px 3px 10px; background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default); border-radius: 14px; max-width: 320px; }
    .sq__blog-chip-title { color: var(--mj-text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 12px; }
    .sq__blog-chip-x { background: transparent; border: none; cursor: pointer; color: var(--mj-text-secondary); width: 20px; height: 20px; padding: 0; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
    .sq__blog-chip-x:hover { background: var(--mj-bg-page); color: var(--mj-text-primary); }
    .sq__blog-chips-clear { margin-left: auto; }

    .sq__blog-list { max-height: 320px; overflow-y: auto; background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default); border-radius: 6px; }
    .sq__blog-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px; cursor: pointer; border-bottom: 1px solid var(--mj-border-default); }
    .sq__blog-item:last-child { border-bottom: none; }
    .sq__blog-item:hover { background: var(--mj-bg-page); }
    .sq__blog-item--selected { background: color-mix(in srgb, var(--mj-brand-primary) 8%, transparent); }
    .sq__blog-item--selected:hover { background: color-mix(in srgb, var(--mj-brand-primary) 12%, transparent); }
    .sq__blog-item-check { font-size: 16px; color: var(--mj-text-disabled); margin-top: 1px; }
    .sq__blog-item--selected .sq__blog-item-check { color: var(--mj-brand-primary); }
    .sq__blog-item-main { flex: 1; min-width: 0; }
    .sq__blog-item-title { font-size: 14px; font-weight: 600; color: var(--mj-text-primary); margin-bottom: 2px; display: flex; align-items: center; gap: 8px; }
    .sq__blog-status { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 7px; border-radius: 999px; background: var(--mj-bg-tertiary, rgba(0,0,0,0.06)); color: var(--mj-text-secondary, #555); }
    .sq__blog-item-meta { font-size: 12px; color: var(--mj-text-secondary); line-height: 1.4; }

    .sq__gen-progress { margin-top: 12px; padding: 10px 14px; background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 13px; color: var(--mj-text-secondary); display: flex; align-items: center; gap: 8px; }
    .sq__gen-error { margin-top: 12px; padding: 10px 14px; background: var(--mj-status-error-bg); color: var(--mj-status-error); border-radius: 6px; font-size: 13px; display: flex; align-items: flex-start; gap: 8px; }
    .sq__gen-error-text { flex: 1; white-space: pre-wrap; }

    /* Queue */
    .sq__queue { }
    .sq__queue-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; }
    .sq__queue-title { font-size: 16px; font-weight: 600; margin: 0; color: var(--mj-text-primary); }
    .sq__queue-count { font-size: 12px; color: var(--mj-text-disabled); }
    .sq__empty { text-align: center; padding: 40px 20px; color: var(--mj-text-secondary); }
    .sq__empty i { font-size: 24px; display: block; margin-bottom: 8px; }
    .sq__empty-small { padding: 16px; text-align: center; color: var(--mj-text-disabled); font-size: 13px; }

    .sq__row { border: 1px solid var(--mj-border-default); border-radius: 8px; padding: 14px; margin-bottom: 10px; background: var(--mj-bg-surface); transition: border-color 0.15s, box-shadow 0.15s; }
    .sq__row:hover { border-color: var(--mj-brand-primary); }
    .sq__row--editing { border-color: var(--mj-brand-primary); box-shadow: 0 0 0 1px var(--mj-brand-primary); }
    .sq__row-head { font-size: 12px; color: var(--mj-text-secondary); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
    .sq__row-employee { font-weight: 600; color: var(--mj-text-primary); }
    .sq__row-sep { color: var(--mj-text-disabled); }
    .sq__row-blog { color: var(--mj-text-secondary); }
    .sq__row-platform { color: var(--mj-text-disabled); }
    .sq__row-body { font-size: 13.5px; color: var(--mj-text-primary); line-height: 1.5; white-space: pre-wrap; margin-bottom: 12px; }
    .sq__row-image { display: block; max-width: 480px; width: 100%; height: auto; border-radius: 6px; border: 1px solid var(--mj-border-default); margin-bottom: 12px; }
    .sq__row-edit { margin-bottom: 12px; }
    .sq__row-textarea { width: 100%; padding: 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 13px; font-family: inherit; resize: vertical; box-sizing: border-box; background: var(--mj-bg-page); color: var(--mj-text-primary); line-height: 1.6; }
    .sq__row-textarea:focus { border-color: var(--mj-brand-primary); outline: none; }
    .sq__row-edit-meta { font-size: 11px; color: var(--mj-text-disabled); margin-top: 4px; text-align: right; }
    .sq__row-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .sq__row-error { font-size: 12px; color: var(--mj-status-error); margin-left: 8px; }

    /* Buttons (shared) */
    .sq__btn { padding: 8px 16px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 13px; cursor: pointer; background: var(--mj-bg-surface); color: var(--mj-text-primary); display: inline-flex; align-items: center; gap: 6px; font-family: inherit; }
    .sq__btn:hover:not(:disabled) { background: var(--mj-bg-page); }
    .sq__btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .sq__btn--primary { background: var(--mj-brand-primary); color: var(--mj-text-inverse); border-color: var(--mj-brand-primary); }
    .sq__btn--primary:hover:not(:disabled) { filter: brightness(1.1); }
    .sq__btn--small { padding: 5px 12px; font-size: 12px; }
    `]
})
export class SocialQueueSectionComponent {
    private cdr = inject(ChangeDetectorRef);

    @Input() blogs: marketingBlogEntity[] = [];
    @Input() socialPosts: marketingSocialPostEntity[] = [];
    @Input() contentStatuses: marketingContentStatusEntity[] = [];
    @Input() companyId = '';

    @Output() contentSaved = new EventEmitter<void>();

    // Self-loaded profiles (keeping the known-working user-social pattern rather than piggy-backing
    // on the parent dashboard's batch RunViews, which didn't surface this entity previously).
    employeeSocialProfiles: EmployeeSocialProfileRow[] = [];
    loadingProfiles = false;
    private lastLoadedCompanyId = '';

    // Generate form state
    selectedProfileId = '';
    selectedBlogIds: string[] = [];
    blogSearch = '';
    variationCount = 5;
    generating = false;
    generationProgress = '';
    generateError = '';

    // Tone editor state
    toneEditorOpen = false;
    toneEditBody = '';
    toneSaving = false;
    toneError = '';

    // Queue row state
    editingPostId: string | null = null;
    editingBody = '';
    rowState: Record<string, RowState> = {};
    rowAction: Record<string, 'approve' | 'reject' | 'edit'> = {};
    rowError: Record<string, string> = {};

    async ngOnInit(): Promise<void> {
        await this.loadProfiles();
    }

    async ngOnChanges(): Promise<void> {
        if (this.companyId && this.companyId !== this.lastLoadedCompanyId) {
            await this.loadProfiles();
        }
    }

    private async loadProfiles(): Promise<void> {
        if (!this.companyId) {
            this.loadingProfiles = false;
            return;
        }
        this.loadingProfiles = true;
        try {
            const rv = new RunView();
            const result = await rv.RunView<EmployeeSocialProfileRow>({
                EntityName: 'Employee Social Profiles',
                ExtraFilter: `CompanyID='${this.companyId}' AND Status='Active'`,
                ResultType: 'simple',
                Fields: ['ID', 'EmployeeID', 'DisplayName', 'ToneNotes', 'Status', 'SocialPlatformID'],
            });
            if (result.Success) {
                this.employeeSocialProfiles = result.Results;
                this.lastLoadedCompanyId = this.companyId;
                if (!this.selectedProfileId && this.employeeSocialProfiles.length === 1) {
                    this.selectedProfileId = this.employeeSocialProfiles[0].ID;
                }
            } else {
                console.error('SocialQueue: load profiles failed', result);
            }
        } catch (error) {
            console.error('SocialQueue: load profiles threw', error);
        } finally {
            this.loadingProfiles = false;
            this.cdr.detectChanges();
        }
    }

    get canGenerate(): boolean {
        return !!this.selectedProfileId && this.selectedBlogIds.length > 0 && this.variationCount >= 1 && this.variationCount <= 20;
    }

    get filteredBlogs(): marketingBlogEntity[] {
        // Show every blog the company has, regardless of publish status — drafts and
        // unapproved posts are valid sources for social variations during pilots and
        // pre-launch reviews. Sort by PublishDate when set, otherwise __mj_CreatedAt.
        const recent = this.blogs
            .slice()
            .sort((a, b) => {
                const aDate = new Date(a.Get('PublishDate') ?? a.Get('__mj_CreatedAt') ?? 0).getTime();
                const bDate = new Date(b.Get('PublishDate') ?? b.Get('__mj_CreatedAt') ?? 0).getTime();
                return bDate - aDate;
            });
        if (!this.blogSearch.trim()) return recent.slice(0, 20);
        const q = this.blogSearch.toLowerCase();
        return recent.filter(b =>
            b.Title?.toLowerCase().includes(q) || b.Get('Summary')?.toLowerCase().includes(q),
        ).slice(0, 20);
    }

    get selectedBlogEntities(): marketingBlogEntity[] {
        return this.selectedBlogIds
            .map(id => this.blogs.find(b => b.ID === id))
            .filter((b): b is marketingBlogEntity => !!b);
    }

    isBlogSelected(blogId: string): boolean {
        return this.selectedBlogIds.includes(blogId);
    }

    toggleBlog(blogId: string): void {
        const idx = this.selectedBlogIds.indexOf(blogId);
        if (idx >= 0) {
            this.selectedBlogIds = this.selectedBlogIds.filter(id => id !== blogId);
        } else {
            this.selectedBlogIds = [...this.selectedBlogIds, blogId];
        }
    }

    clearAllBlogs(): void {
        this.selectedBlogIds = [];
    }

    get selectedProfile(): EmployeeSocialProfileRow | null {
        return this.selectedProfileId ? this.employeeSocialProfiles.find(p => p.ID === this.selectedProfileId) ?? null : null;
    }

    get selectedProfileName(): string {
        return this.selectedProfile?.DisplayName ?? '';
    }

    get selectedProfileTone(): string {
        return this.selectedProfile?.ToneNotes ?? '';
    }

    onProfileChange(): void {
        // Close tone editor if the user switches profiles mid-edit.
        this.toneEditorOpen = false;
        this.toneEditBody = '';
        this.toneError = '';
    }

    openToneEditor(): void {
        this.toneEditBody = this.selectedProfileTone;
        this.toneError = '';
        this.toneEditorOpen = true;
    }

    cancelToneEdit(): void {
        this.toneEditorOpen = false;
        this.toneEditBody = '';
        this.toneError = '';
    }

    async saveTone(): Promise<void> {
        if (!this.selectedProfileId) return;
        this.toneSaving = true;
        this.toneError = '';
        this.cdr.detectChanges();

        try {
            const rv = new RunView();
            const loaded = await rv.RunView<marketingEmployeeSocialProfileEntity>({
                EntityName: 'Employee Social Profiles',
                ExtraFilter: `ID='${this.selectedProfileId}'`,
                ResultType: 'entity_object',
                MaxRows: 1,
            });
            if (!loaded.Success || loaded.Results.length === 0) {
                this.toneError = 'Could not load profile to save';
                return;
            }
            const profile = loaded.Results[0];
            profile.ToneNotes = this.toneEditBody;
            const saved = await profile.Save();
            if (!saved) {
                this.toneError = profile.LatestResult?.Message ?? 'Save returned false';
                return;
            }

            // Reflect the saved value locally so the UI updates without a full dashboard reload.
            const local = this.employeeSocialProfiles.find(p => p.ID === this.selectedProfileId);
            if (local) local.ToneNotes = this.toneEditBody;

            this.toneEditorOpen = false;
            this.toneEditBody = '';
        } catch (error) {
            this.toneError = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
        } finally {
            this.toneSaving = false;
            this.cdr.detectChanges();
        }
    }

    get draftPosts(): marketingSocialPostEntity[] {
        const draftId = this.contentStatuses.find(s => s.Name === 'Draft')?.ID;
        if (!draftId) return [];
        return this.socialPosts
            .filter(p => p.Get('ContentStatusID') === draftId && p.Get('SocialProfileID'))
            .slice()
            .sort((a, b) => {
                const aDate = new Date(a.Get('__mj_CreatedAt') ?? 0).getTime();
                const bDate = new Date(b.Get('__mj_CreatedAt') ?? 0).getTime();
                return bDate - aDate;
            });
    }

    employeeNameFor(profileId: string | null | undefined): string {
        if (!profileId) return 'Unknown';
        return this.employeeSocialProfiles.find(p => p.ID === profileId)?.DisplayName ?? 'Unknown';
    }

    blogTitleFor(blogId: string | null | undefined): string {
        if (!blogId) return 'No blog';
        return this.blogs.find(b => b.ID === blogId)?.Title ?? 'Unknown blog';
    }

    async generate(): Promise<void> {
        if (!this.canGenerate) return;
        this.generating = true;
        this.generateError = '';
        this.generationProgress = '';
        this.cdr.detectChanges();

        try {
            const rv = new RunView();
            const actionResult = await rv.RunView<{ ID: string }>({
                EntityName: 'MJ: Actions',
                ExtraFilter: `Name='Generate And Queue Variations'`,
                ResultType: 'simple',
                Fields: ['ID'],
                MaxRows: 1,
            });

            if (!actionResult.Success || actionResult.Results.length === 0) {
                this.generateError = 'Generate And Queue Variations action not found. Is the Marketing package loaded?';
                return;
            }

            const actionId = actionResult.Results[0].ID;
            const gql = `mutation RunAction($input: RunActionInput!) {
                RunAction(input: $input) { Success Message ResultCode }
            }`;

            const blogIds = [...this.selectedBlogIds];
            const perBlogErrors: string[] = [];
            let successfulBlogs = 0;

            for (let i = 0; i < blogIds.length; i++) {
                const blogId = blogIds[i];
                const blogTitle = this.blogs.find(b => b.ID === blogId)?.Title ?? blogId;
                this.generationProgress = `Blog ${i + 1} of ${blogIds.length}: "${blogTitle}"...`;
                this.cdr.detectChanges();

                try {
                    const result = await GraphQLDataProvider.ExecuteGQL(gql, {
                        input: {
                            ActionID: actionId,
                            Params: [
                                { Name: 'BlogID', Value: blogId, Type: 'Input' },
                                { Name: 'CompanyID', Value: this.companyId, Type: 'Input' },
                                { Name: 'EmployeeSocialProfileID', Value: this.selectedProfileId, Type: 'Input' },
                                { Name: 'VariationCount', Value: String(this.variationCount), Type: 'Input' },
                            ],
                        },
                    });

                    const response = result?.RunAction;
                    if (response?.Success) {
                        successfulBlogs++;
                    } else {
                        perBlogErrors.push(`"${blogTitle}": ${response?.Message || 'Unknown error'}`);
                    }
                } catch (err) {
                    perBlogErrors.push(`"${blogTitle}": ${err instanceof Error ? err.message : 'Unknown error'}`);
                }
            }

            if (perBlogErrors.length > 0) {
                const prefix = successfulBlogs === 0
                    ? 'All generations failed.'
                    : `Completed ${successfulBlogs} of ${blogIds.length} blog(s); ${perBlogErrors.length} failed:`;
                this.generateError = `${prefix}\n${perBlogErrors.join('\n')}`;
            }

            // Clear selection after a run so the user can start fresh.
            this.selectedBlogIds = [];

            // Refresh the queue regardless — partial successes still produced drafts.
            this.contentSaved.emit();
        } catch (error) {
            this.generateError = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
        } finally {
            this.generating = false;
            this.generationProgress = '';
            this.cdr.detectChanges();
        }
    }

    async approve(post: marketingSocialPostEntity): Promise<void> {
        const postId = post.ID;
        const profileId = post.Get('SocialProfileID');
        if (!profileId) {
            this.rowError[postId] = 'Post has no SocialProfileID — cannot approve';
            return;
        }
        this.rowState[postId] = 'busy';
        this.rowAction[postId] = 'approve';
        this.rowError[postId] = '';
        this.cdr.detectChanges();

        try {
            const rv = new RunView();
            const actionResult = await rv.RunView<{ ID: string }>({
                EntityName: 'MJ: Actions',
                ExtraFilter: `Name='Publish Social Post'`,
                ResultType: 'simple',
                Fields: ['ID'],
                MaxRows: 1,
            });

            if (!actionResult.Success || actionResult.Results.length === 0) {
                this.rowError[postId] = 'Publish Social Post action not found';
                this.rowState[postId] = 'error';
                return;
            }

            const gql = `mutation RunAction($input: RunActionInput!) {
                RunAction(input: $input) { Success Message ResultCode }
            }`;

            const result = await GraphQLDataProvider.ExecuteGQL(gql, {
                input: {
                    ActionID: actionResult.Results[0].ID,
                    Params: [
                        { Name: 'SocialPostID', Value: postId, Type: 'Input' },
                        { Name: 'CompanyID', Value: this.companyId, Type: 'Input' },
                        { Name: 'EmployeeSocialProfileID', Value: profileId, Type: 'Input' },
                    ],
                },
            });

            const response = result?.RunAction;
            if (!response?.Success) {
                this.rowError[postId] = `Publish failed: ${response?.Message || 'Unknown error'}`;
                this.rowState[postId] = 'error';
                return;
            }

            this.rowState[postId] = 'idle';
            this.contentSaved.emit();
        } catch (error) {
            this.rowError[postId] = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
            this.rowState[postId] = 'error';
        } finally {
            this.cdr.detectChanges();
        }
    }

    async reject(post: marketingSocialPostEntity): Promise<void> {
        const postId = post.ID;
        const rejected = this.contentStatuses.find(s => s.Name === 'Rejected');
        if (!rejected) {
            this.rowError[postId] = 'Rejected ContentStatus not found';
            return;
        }
        this.rowState[postId] = 'busy';
        this.rowAction[postId] = 'reject';
        this.rowError[postId] = '';
        this.cdr.detectChanges();

        try {
            post.Set('ContentStatusID', rejected.ID);
            const saved = await post.Save();
            if (!saved) {
                this.rowError[postId] = post.LatestResult?.Message ?? 'Save returned false';
                this.rowState[postId] = 'error';
                return;
            }
            this.rowState[postId] = 'idle';
            this.contentSaved.emit();
        } catch (error) {
            this.rowError[postId] = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
            this.rowState[postId] = 'error';
        } finally {
            this.cdr.detectChanges();
        }
    }

    startEdit(post: marketingSocialPostEntity): void {
        this.editingPostId = post.ID;
        this.editingBody = post.Get('Body') ?? '';
        this.rowError[post.ID] = '';
    }

    cancelEdit(): void {
        this.editingPostId = null;
        this.editingBody = '';
    }

    async saveEdit(post: marketingSocialPostEntity): Promise<void> {
        const postId = post.ID;
        this.rowState[postId] = 'busy';
        this.rowAction[postId] = 'edit';
        this.rowError[postId] = '';
        this.cdr.detectChanges();

        try {
            post.Set('Body', this.editingBody);
            const saved = await post.Save();
            if (!saved) {
                this.rowError[postId] = post.LatestResult?.Message ?? 'Save returned false';
                this.rowState[postId] = 'error';
                return;
            }
            this.rowState[postId] = 'idle';
            this.editingPostId = null;
            this.editingBody = '';
            this.contentSaved.emit();
        } catch (error) {
            this.rowError[postId] = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
            this.rowState[postId] = 'error';
        } finally {
            this.cdr.detectChanges();
        }
    }
}
